import { Card, Stack, Typography, Box, Avatar, Skeleton } from "@mui/material";
import React, { FC, useCallback } from "react";

import { InstagramProfile } from "src/types/analysis";
import { api } from "src/config";
import { useTranslation } from "react-i18next";
import { tokens } from "src/locales/tokens";

interface InstagramSearchFormProps {
  account?: InstagramProfile;
}

export const InstagramProfileCard: FC<InstagramSearchFormProps> = (props) => {
  const { account } = props;

  const getImageUrl = useCallback((url: string) => {
    return `${api}/image/download?url=` + encodeURIComponent(url);
  }, []);

  const { t } = useTranslation();

  return (
    <>
      <Card>
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2}
          sx={{
            p: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {account && (
              <Avatar
                alt="Applicant"
                sizes="100%"
                src={getImageUrl(account.data.profilePicUrl)}
                sx={{
                  border: "3px solid var(--mui-palette-background-paper)",
                  height: "150px",
                  width: "150px",
                }}
              />
            )}
            {!account && (
              <Skeleton
                variant="circular"
                width={150}
                height={150}
              />
            )}
          </Box>
          <Box
            sx={{
              width: "100%",
            }}
          >
            <Stack
              spacing={8}
              direction={"row"}
              justifyContent={{
                xs: "space-between",
                sm: "flex-start",
              }}
              sx={{
                width: "100%",
                mt: 1,
              }}
            >
              <Stack>
                {account && (
                  <Typography variant="h5">
                    {new Intl.NumberFormat("en-US").format(
                      account.data.postsCount
                    )}
                  </Typography>
                )}
                {!account && <Skeleton />}
                <Typography
                  color="text.secondary"
                  component="h4"
                  variant="overline"
                >
                  {t(tokens.instagram.profile.posts)}
                </Typography>
              </Stack>
              <Stack>
                {account && (
                  <Typography variant="h5">
                    {new Intl.NumberFormat("en-US").format(
                      account.data.followersCount
                    )}
                  </Typography>
                )}
                {!account && <Skeleton />}
                <Typography
                  color="text.secondary"
                  component="h4"
                  variant="overline"
                >
                  {t(tokens.instagram.profile.followers)}
                </Typography>
              </Stack>
              <Stack>
                {account && (
                  <Typography variant="h5">
                    {new Intl.NumberFormat("en-US").format(
                      account.data.followsCount
                    )}
                  </Typography>
                )}
                {!account && <Skeleton />}
                <Typography
                  color="text.secondary"
                  component="h4"
                  variant="overline"
                >
                  {t(tokens.instagram.profile.following)}
                </Typography>
              </Stack>
            </Stack>
            <Box
              sx={{
                mt: 1,
              }}
            >
              {account && (
                <Typography variant="subtitle1">
                  {account.data.fullName}
                </Typography>
              )}
              {!account && <Skeleton />}
            </Box>
            <Box
              sx={{
                mt: 0,
              }}
            >
              {account && (
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                >
                  <a
                    href={account.data.url}
                    target="_blank"
                    rel="noreferrer noopener nofollow"
                  >
                    {account.username}
                  </a>
                </Typography>
              )}
              {!account && <Skeleton />}
            </Box>
            <Box
              sx={{
                mt: 1,
              }}
            >
              {account && (
                <Typography variant="body1">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: account.data.biography.replaceAll("\n", "<br>"),
                    }}
                  />
                </Typography>
              )}
              {!account && <Skeleton />}
            </Box>
            <Box
              sx={{
                mt: 1,
              }}
            >
              {account && (
                <Typography variant="body1">
                  <a
                    href={account.data.externalUrl}
                    target="_blank"
                    rel="noreferrer noopener nofollow"
                  >
                    {account.data.externalUrl}
                  </a>
                </Typography>
              )}
              {!account && <Skeleton />}
            </Box>
          </Box>
        </Stack>
      </Card>
    </>
  );
};
